import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import logoImage from '../../assets/images/logo.png';
import {
  AppBar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useTheme,
  Link
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Help as HelpIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import EnvironmentBadge from './EnvironmentBadge';

interface HeaderProps {
  toolTitle: string;
}

const Header: React.FC<HeaderProps> = ({ toolTitle }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { currentUser: user } = useSelector((state: RootState) => state.user);
  const { currentCompany } = useSelector((state: RootState) => state.company);
  const theme = useTheme();
  const navigate = useNavigate();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const labelSx = {
    borderRadius: '2px',
    display: 'inline-block',
    px: 1,
    py: 0.5,
    fontSize: '10px',
    marginRight: '5px',
    cursor: 'pointer',
    userSelect: 'none' as const,
    '&:hover': { opacity: 0.85 },
  };
  
  return (
    <AppBar
      position="fixed"
      sx={{ width: '100%' }}
    >
      <Toolbar
        sx={{
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            objectPosition: 'left'
          }}
        >
          <Box 
            component="img"
            src={logoImage}
            alt="SmartSkillTalk"
            sx={{ 
              cursor: 'pointer',
              objectFit: 'contain',
              marginRight: '8px',
            }}
            onClick={() => navigate('/')}
          />
          <EnvironmentBadge sx={{ marginRight: '8px' }} />
          <Typography 
            component="p" 
            className="catch-phrase-text"
            sx={{ 
              color: theme.palette.smartSkillTalk.orange,
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '1.5em',
              letterSpacing: '0.1em',
              display: { xs:'none', md: 'inline' }
            }}
          >
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end' 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center' 
          }}>
            <Link 
              href="/help" 
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '1.5em',
                color: theme.palette.smartSkillTalk.linkBlue,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <HelpIcon sx={{ 
                width: '20px', 
                height: '20px', 
                marginRight: '5px',
              }} />
              <Typography
                component="span"
                sx={{
                  display: { xs: 'none', md: 'inline' }
                }}
              >
                ヘルプ・お問い合わせ
              </Typography>
            </Link>
          </Box>

          {isAuthenticated && (
            <Box sx={{ 
              textAlign: 'right',
              marginTop: '10px',
              whiteSpace: 'nowrap',
              display: { xs:'none',md: 'block' }
            }}>
              {user?.userRole === 'ADMIN' && (
                user?.locked ? (
                  <Box
                    sx={{ ...labelSx, bgcolor: '#FF6B35', color: 'white' }}
                    onClick={() => setInfoDialogOpen(true)}
                    title="クリックして詳細を表示"
                  >
                    特権管理者
                  </Box>
                ) : (
                  <Box
                    sx={{ ...labelSx, bgcolor: '#FCA600', color: 'white' }}
                    onClick={() => setInfoDialogOpen(true)}
                    title="クリックして詳細を表示"
                  >
                    管理者
                  </Box>
                )
              )}
              {user?.userRole === 'SYSTEM_ADMIN' && (
                <Box
                  sx={{ ...labelSx, bgcolor: '#E91E63', color: 'white' }}
                  onClick={() => setInfoDialogOpen(true)}
                  title="クリックして詳細を表示"
                >
                  システム管理者
                </Box>
              )}
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'inline-block',
                  marginRight: '5px',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '1.5em',
                  letterSpacing: '0.1em',
                  color: theme.palette.smartSkillTalk.linkBlue 
                }}
              >
                {(() => {
                  const companyName = currentCompany?.name;
                  const department = user?.department;
                  
                  if (companyName && department) {
                    return `${companyName} ${department}`;
                  } else if (companyName) {
                    return companyName;
                  } else if (department) {
                    return department;
                  } else {
                    return '';
                  }
                })()}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'inline-block',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '1.5em',
                  letterSpacing: '0.1em',
                  color: theme.palette.smartSkillTalk.linkBlue 
                }}
              >
                {user?.displayName || 'ユーザー'}<span style={{ fontSize: '14px' }}>さん</span>
              </Typography>
            </Box>
          )}
        </Box>
      </Toolbar>
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.palette.smartSkillTalk.linkBlue,
        padding: '13px 40px',
      }}>
        <IconButton 
          color="inherit" 
          onClick={() => navigate('/')}
          sx={{ padding: 0 }}
        >
          <HomeIcon sx={{ 
            width: '26px', 
            height: '26px', 
            color: '#FFFFFF' 
          }} />
        </IconButton>
        <Typography sx={{
          fontSize: '20px',
          fontWeight: 400,
          lineHeight: '1.5em',
          color: '#FFFFFF'
        }}>
          { toolTitle || 'none' }
        </Typography>
        {["ADMIN", "SYSTEM_ADMIN"].includes(user?.userRole || '') ? (
          <IconButton 
            color="inherit"
            onClick={() => navigate('/admin')}
            sx={{ padding: 0 }}
          >
            <SettingsIcon sx={{ 
              width: '26px', 
              height: '26px', 
              color: '#FFFFFF' 
            }} />
          </IconButton>
        ): (
          <IconButton 
            sx={{ padding: 0 }}
          >
            <SettingsIcon sx={{ 
              width: '0px', 
              height: '0px', 
              color: theme.palette.smartSkillTalk.linkBlue,
            }} />
          </IconButton>
        )}
      </Toolbar>

      {/* 管理者情報確認ダイアログ */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 500, pb: 1 }}>
          アカウント情報
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={0} divider={<Divider />}>
            {[
              {
                label: 'ログインID',
                value: user?.givenUserId || '—',
              },
              {
                label: '所属',
                value: (() => {
                  const depts = user?.departments && user.departments.length > 0
                    ? user.departments
                    : user?.department
                      ? [user.department]
                      : [];
                  return depts.length > 0 ? depts : null;
                })(),
              },
              {
                label: '管理範囲',
                value: user?.managementScope || '全体',
              },
              {
                label: 'プロファイル',
                value: user?.profile?.name || '—',
              },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ py: 1.2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {label}
                </Typography>
                {Array.isArray(value) ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((v) => (
                      <Chip key={v} label={v} size="small" variant="outlined" />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {value}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Header;
